import { Col, Form, Row } from 'react-bootstrap';

import { EditNumber } from 'components/forms/EditNumber';
import {
  daysInMonth,
  FISCAL_ANCHOR_RULE_OPTIONS,
  FISCAL_MONTH_OPTIONS,
  ruleUsesAnchorDay,
  type FiscalAnchorRule,
  type FiscalPeriodConfig,
} from 'utils/fiscalPeriods';

import type { FiscalPeriodPatch } from './shared';

interface Props {
  readonly config: FiscalPeriodConfig;
  readonly patch: FiscalPeriodPatch;
}

/** Anchor rule, month, and optional day controls for the fiscal calendar. */
export const AnchorRuleFields = ({ config, patch }: Props) => {
  const showDay = ruleUsesAnchorDay(config.anchor_rule);
  const maxDay = daysInMonth(config.anchor_month);

  return (
    <>
      <h6 className="mb-2">How the year is anchored</h6>
      <Row className="g-3 mb-3">
        <Col xs={12} md={6}>
          <Form.Group controlId="fiscal-anchor-rule">
            <Form.Label>Anchor rule</Form.Label>
            <Form.Select value={config.anchor_rule} onChange={(e) => patch({ anchor_rule: e.target.value as FiscalAnchorRule })}>
              {FISCAL_ANCHOR_RULE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">{FISCAL_ANCHOR_RULE_OPTIONS.find((o) => o.value === config.anchor_rule)?.hint}</Form.Text>
          </Form.Group>
        </Col>
        <Col xs={12} md={showDay ? 3 : 6}>
          <Form.Group controlId="fiscal-anchor-month">
            <Form.Label>Anchor month</Form.Label>
            <Form.Select value={config.anchor_month} onChange={(e) => patch({ anchor_month: Number(e.target.value) })}>
              {FISCAL_MONTH_OPTIONS.map((monthOption) => (
                <option key={monthOption.value} value={monthOption.value}>
                  {monthOption.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">Need not be January — retail often uses end of January; others April, July, October.</Form.Text>
          </Form.Group>
        </Col>
        {showDay ? (
          <Col xs={12} md={3}>
            <Form.Group controlId="fiscal-anchor-day">
              <Form.Label>Anchor day</Form.Label>
              <EditNumber
                value={config.anchor_day}
                min={1}
                max={maxDay}
                onChange={(day) => {
                  // Blank / non-numeric → null from EditNumber; keep the last valid day.
                  if (day !== null) patch({ anchor_day: day });
                }}
              />
              {config.anchor_month === 2 ? <Form.Text className="text-muted">Day 29 is used in leap years and treated as 28 in common years.</Form.Text> : null}
            </Form.Group>
          </Col>
        ) : null}
      </Row>
    </>
  );
};
